// ObjectDashboard.js
import { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../config';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../i18n/LanguageContext';
import { translatePropertyType } from '../../i18n/translations';
import './ObjectDashboard.css';

function ObjectDashboard() {
  const { t, language } = useLanguage();
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchPropertyType, setSearchPropertyType] = useState('');
  const [searchLan, setSearchLan] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const backend_url = config.backend_url;

  useEffect(() => {
    fetchObjects();
  }, []);

  const fetchObjects = async (page = 1, address = '', propertyType = '', lan = '') => {
    try {
      setLoading(true);
      let url = `${backend_url}/api/objects`;
      
      // Always use pagination - check if search parameters are provided
      if (address || propertyType || lan) {
        url = `${backend_url}/api/objects/search?page=${page}&per_page=12`;
        if (address) url += `&address=${encodeURIComponent(address)}`;
        if (propertyType) url += `&property_type=${encodeURIComponent(propertyType)}`;
        if (lan) url += `&lan=${encodeURIComponent(lan)}`;
        setSearchPerformed(true);
      } else {
        // Use pagination for regular object listing too
        url = `${backend_url}/api/objects?page=${page}&per_page=12`;
        setSearchPerformed(false);
      }
      
      const response = await axios.get(url, {
        withCredentials: true
      });
      
      // Handle response format - check if response has pagination structure
      if (response.data.objects !== undefined) {
        // Paginated response format
        setObjects(response.data.objects);
        setTotalPages(response.data.pages || 1);
        setCurrentPage(response.data.current_page || 1);
        console.log('Pagination data:', { 
          total: response.data.total, 
          pages: response.data.pages, 
          currentPage: response.data.current_page,
          objectsCount: response.data.objects.length 
        });
      } else {
        // Legacy non-paginated response format
        setObjects(response.data);
        setTotalPages(1);
        setCurrentPage(1);
        console.log('Legacy format - no pagination');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching objects:', error);
      setError(t('objectDashboard.loadError'));
      setLoading(false);
    }
  };

  // Generate a static map image URL for a given longitude and latitude
  const getStaticMapImageUrl = (longitude, latitude) => {
    // Validate coordinates
    if (!longitude || !latitude) return null;
    
    const lng = parseFloat(longitude);
    const lat = parseFloat(latitude);
    
    if (isNaN(lng) || isNaN(lat)) return null;
    
    // Mapbox Static Images API URL
    // Format: https://api.mapbox.com/styles/v1/{username}/{style_id}/static/pin-l+{color}({longitude},{latitude})/{longitude},{latitude},{zoom}/{width}x{height}@2x?access_token={access_token}
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l-marker+f74e4e(${lng},${lat})/${lng},${lat},14,0/400x200@2x?access_token=${config.mapbox_token}&logo=false`;
  };

  // Function to truncate text with a character limit
  const truncateText = (text, limit) => {
    if (!text) return '';
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  };

  // Format property area and number of rooms for display
  const formatPropertyDetails = (object) => {
    const details = [];
    
    if (object.property_type) {
      details.push(translatePropertyType(object.property_type, language));
    }
    
    if (object.number_of_rooms) {
      details.push(`${object.number_of_rooms} ${t('objectForm.room')}`);
    }
    
    if (object.area) {
      details.push(`${object.area} m²`);
    }
    
    return details.join(' • ');
  };

  // Remove trailing ", Sweden" from addresses
  const stripCountryFromAddress = (address) => {
    if (!address || typeof address !== 'string') return address;
    return address.replace(/,\s*Sweden\s*$/i, '').trim();
  };

  // Function to display address based on contact info display setting
  const formatAddress = (object) => {
    if (object.display_contact_info) {
      return stripCountryFromAddress(object.address) || t('objectDashboard.missingAddress');
    } else {
      return stripCountryFromAddress(object.hidden_address) || t('objectDashboard.missingAddress');
    }
  };

  // Search handler functions
  const handleSearch = (e) => {
    e.preventDefault();
    fetchObjects(1, searchAddress, searchPropertyType, searchLan);
  };

  const handleReset = () => {
    setSearchAddress('');
    setSearchPropertyType('');
    setSearchLan('');
    setSearchPerformed(false);
    setCurrentPage(1);
    fetchObjects(1); // Fetch all objects again from page 1
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchObjects(newPage, searchAddress, searchPropertyType, searchLan);
  };

  return (
    <div className="object-dashboard">
      <div className="object-search-section">
        <h2><FontAwesomeIcon icon={faSearch} className="search-icon" /> {t('objectDashboard.filter')}</h2>
        <form onSubmit={handleSearch} className="object-search-form">
          <div className="search-inputs">
            <div className="form-group">
              <label htmlFor="address-search">{t('objectDashboard.city')}</label>
              <input
                id="address-search"
                type="text"
                placeholder={t('objectDashboard.cityPlaceholder')}
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lan-search">{t('objectDashboard.region')}</label>
              <select
                id="lan-search"
                value={searchLan}
                onChange={(e) => setSearchLan(e.target.value)}
                className="search-input"
              >
                <option value="">{t('objectDashboard.allRegions')}</option>
                <option value="Blekinge">Blekinge län</option>
                <option value="Dalarna">Dalarnas län</option>
                <option value="Gotland">Gotlands län</option>
                <option value="Gävleborg">Gävleborgs län</option>
                <option value="Halland">Hallands län</option>
                <option value="Jämtland">Jämtlands län</option>
                <option value="Jönköping">Jönköpings län</option>
                <option value="Kalmar">Kalmar län</option>
                <option value="Kronoberg">Kronobergs län</option>
                <option value="Norrbotten">Norrbottens län</option>
                <option value="Skåne">Skåne län</option>
                <option value="Stockholm">Stockholms län</option>
                <option value="Södermanland">Södermanlands län</option>
                <option value="Uppsala">Uppsala län</option>
                <option value="Värmland">Värmlands län</option>
                <option value="Västerbotten">Västerbottens län</option>
                <option value="Västernorrland">Västernorrlands län</option>
                <option value="Västmanland">Västmanlands län</option>
                <option value="Västra Götaland">Västra Götalands län</option>
                <option value="Örebro">Örebro län</option>
                <option value="Östergötland">Östergötlands län</option>
              </select>
            </div>
          </div>
          
          <div className="search-buttons">
            <button type="submit" className="search-button">{t('objectDashboard.search')}</button>
            <button type="button" onClick={handleReset} className="reset-button">{t('objectDashboard.reset')}</button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="loading-indicator" style={{ 
          animation: 'none'
        }}>
          <p>{t('objectDashboard.loading')}</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
        </div>
      ) : objects.length === 0 ? (
        searchPerformed ? (
          <div className="no-objects-message">
            <p>{t('objectDashboard.noResults')}</p>
          </div>
        ) : (
          <div className="empty-search-state">
            <FontAwesomeIcon icon={faSearch} className="empty-icon" />
            <p>{t('objectDashboard.emptyTitle')}</p>
            <p className="empty-subtext">{t('objectDashboard.emptySubtext')}</p>
          </div>
        )
      ) : (
        <>
          <div className="object-grid">
            {objects.map((object) => (
              <div key={object.id} className="object-card">
                <div className="object-image-container">
                  {object.first_image_url ? (
                    <img
                      src={object.first_image_url}
                      alt={object.address || t('objectDashboard.propertyAlt')}
                      className="object-image"
                    />
                  ) : object.latitude && object.longitude ? (
                    <img
                      src={getStaticMapImageUrl(object.longitude, object.latitude)}
                      alt={t('objectDashboard.mapAlt', { address: object.address || t('objectDashboard.propertyAlt').toLowerCase() })}
                      className="object-map-image"
                      onError={(e) => {
                        e.target.onerror = null; 
                        e.target.src = "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%23f9f9f9'/%3E%3Ctext x='20' y='20' font-family='Arial' font-size='14' text-anchor='middle' alignment-baseline='middle' fill='%23999999'%3EKarta ej tillgänglig%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  ) : (
                    <div className="no-image">
                      <span>{t('objectDashboard.noImage')}</span>
                    </div>
                  )}
                </div>
                
                <div className="object-content">
                  <h3 className="object-address">{formatAddress(object)}</h3>
                  
                  <div className="object-details">
                    <p className="property-details">{formatPropertyDetails(object)}</p>
                  </div>
                                  
                  <Link to={`/seller-data/${object.id}`} className="view-more-button">
                    {t('objectDashboard.viewMore')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-button" 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.pageOf', { current: currentPage, total: totalPages })}
              </span>
              <button 
                className="pagination-button" 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ObjectDashboard;
